// frontend/src/services/api.js - Production-ready HTTP client with token refresh

import axios from 'axios';

// ===============================================
// CONFIGURATION & CONSTANTS
// ===============================================

export const STORAGE_KEYS = {
  auth: {
    token: 'auth_token',
    user: 'auth_user',
    lastActivity: 'auth_last_activity',
    rememberMe: 'auth_remember_me',
  },
};

// Environment-based configuration
const API_CONFIG = {
  baseURL: import.meta.env.VITE_API_URL || 'http://localhost:3002/api/v1',
  timeout: 30000,
  retryAttempts: 2,
  retryDelay: 1000,
};

// ===============================================
// AXIOS INSTANCE CREATION
// ===============================================

export const api = axios.create({
  baseURL: API_CONFIG.baseURL,
  timeout: API_CONFIG.timeout,
  headers: {
    'Content-Type': 'application/json',
  },
});

// ===============================================
// TOKEN MANAGEMENT
// ===============================================

let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  
  failedQueue = [];
};

const getStoredToken = () => {
  const token = localStorage.getItem(STORAGE_KEYS.auth.token) || 
                sessionStorage.getItem(STORAGE_KEYS.auth.token);
  
  if (import.meta.env.DEV && token) {
    console.debug('🔑 Token retrieved:', token.substring(0, 20) + '...');
  }
  
  return token;
};

const getStoredUser = () => {
  const userData = localStorage.getItem(STORAGE_KEYS.auth.user) || 
                   sessionStorage.getItem(STORAGE_KEYS.auth.user);
  return userData ? JSON.parse(userData) : null;
};

const isTokenExpired = (token) => {
  if (!token) return true;
  try {
    const base64url = token.split('.')[1];
    const base64 = base64url.replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64 + '='.repeat((4 - base64.length % 4) % 4);
    const payload = JSON.parse(atob(padded));
    const now = Date.now() / 1000;
    return payload.exp < now;
  } catch (error) {
    return true;
  }
};

const clearAuthData = () => {
  localStorage.removeItem(STORAGE_KEYS.auth.token);
  localStorage.removeItem(STORAGE_KEYS.auth.user);
  localStorage.removeItem(STORAGE_KEYS.auth.lastActivity);
  localStorage.removeItem(STORAGE_KEYS.auth.rememberMe);
  
  sessionStorage.removeItem(STORAGE_KEYS.auth.token);
  sessionStorage.removeItem(STORAGE_KEYS.auth.user);
  sessionStorage.removeItem(STORAGE_KEYS.auth.lastActivity);
};

// ===============================================
// UTILITY FUNCTIONS (No dependencies)
// ===============================================

export const isOnline = () => {
  return typeof navigator !== 'undefined' ? navigator.onLine : true;
};

export const generateRequestId = () => {
  return `req_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};

export const getClientInfo = () => {
  return {
    timestamp: Date.now(),
    userAgent: navigator.userAgent.substring(0, 100),
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    language: navigator.language,
  };
};

// ===============================================
// SIMPLE CACHE IMPLEMENTATION
// ===============================================

class SimpleCache {
  constructor(ttl = 5 * 60 * 1000) {
    // 5 minutes default TTL
    this.cache = new Map();
    this.ttl = ttl;
  }

  get(key) {
    const cached = this.cache.get(key);
    if (cached && Date.now() - cached.timestamp < this.ttl) {
      return cached.data;
    }
    this.cache.delete(key);
    return null;
  }

  set(key, data) {
    this.cache.set(key, {
      data,
      timestamp: Date.now(),
    });
    this.cleanup();
  }

  delete(key) {
    this.cache.delete(key);
  }

  clear(pattern = null) {
    if (pattern) {
      const regex = new RegExp(pattern);
      Array.from(this.cache.keys()).forEach(key => {
        if (regex.test(key)) {
          this.cache.delete(key);
        }
      });
    } else {
      this.cache.clear();
    }
  }

  cleanup() {
    if (this.cache.size > 100) {
      const now = Date.now();
      Array.from(this.cache.entries()).forEach(([key, value]) => {
        if (now - value.timestamp > this.ttl) {
          this.cache.delete(key);
        }
      });
    }
  }
}

const cache = new SimpleCache();

// ===============================================
// REQUEST INTERCEPTOR
// ===============================================

api.interceptors.request.use(
  config => {
    try {
      // Add auth token
      const token = getStoredToken();
      if (token && !isTokenExpired(token)) {
        config.headers.Authorization = `Bearer ${token}`;
        if (import.meta.env.DEV) {
          console.debug('🔒 Auth header added to request:', config.url);
        }
      } else if (import.meta.env.DEV) {
        console.warn('⚠️ No valid token for request:', config.url, {
          hasToken: !!token,
          isExpired: token ? isTokenExpired(token) : 'N/A'
        });
      }

      // Add security headers
      config.headers['x-client-info'] = JSON.stringify(getClientInfo());
      config.headers['x-request-id'] = generateRequestId();

      // Update last activity
      const storage = localStorage.getItem(STORAGE_KEYS.auth.token) ? localStorage : sessionStorage;
      storage.setItem(STORAGE_KEYS.auth.lastActivity, Date.now().toString());

      // Log in development
      if (import.meta.env.DEV) {
        console.debug('🔗 API Request:', {
          method: config.method?.toUpperCase(),
          url: config.url,
          hasAuth: !!config.headers.Authorization,
        });
      }
    } catch (error) {
      console.error('Request interceptor error:', error);
    }

    return config;
  },
  error => Promise.reject(error)
);

// ===============================================
// RESPONSE INTERCEPTOR WITH TOKEN REFRESH
// ===============================================

api.interceptors.response.use(
  response => {
    // Log successful responses in development
    if (import.meta.env.DEV) {
      console.debug('✅ API Response:', {
        status: response.status,
        url: response.config.url,
      });
    }
    return response;
  },
  async error => {
    const originalRequest = error.config;

    // Handle 401 Unauthorized with token refresh (but skip for login endpoints)
    if (error.response?.status === 401 && !originalRequest._retry && 
        !originalRequest.url?.includes('/auth/login/')) {
      if (isRefreshing) {
        // If refresh is in progress, queue the request
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then(token => {
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return api(originalRequest);
        }).catch(err => {
          return Promise.reject(err);
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const token = getStoredToken();
        const user = getStoredUser();

        if (!token || !user) {
          throw new Error('No token or user data available');
        }

        // Try to refresh token using session extension
        const refreshResponse = await axios.post(`${API_CONFIG.baseURL}/auth/sessions/extend`, {
          location: await getCurrentLocation().catch(() => null)
        }, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (refreshResponse.data.success && refreshResponse.data.data?.token) {
          const newToken = refreshResponse.data.data.token;
          const newUser = refreshResponse.data.data.user;

          // Update stored data
          const storage = localStorage.getItem(STORAGE_KEYS.auth.token) ? localStorage : sessionStorage;
          storage.setItem(STORAGE_KEYS.auth.token, newToken);
          storage.setItem(STORAGE_KEYS.auth.user, JSON.stringify(newUser));
          storage.setItem(STORAGE_KEYS.auth.lastActivity, Date.now().toString());

          // Process queued requests
          processQueue(null, newToken);

          // Retry original request
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          return api(originalRequest);
        } else {
          throw new Error('Token refresh failed');
        }
      } catch (refreshError) {
        // Refresh failed, clear auth and redirect to login
        processQueue(refreshError, null);
        clearAuthData();
        
        if (typeof window !== 'undefined' && !window.location.pathname.includes('/login')) {
          window.location.href = '/login';
        }
        
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    // Handle 403 Forbidden (Device Security)
    if (error.response?.status === 403) {
      const errorCode = error.response.data?.code;
      console.warn(`🚫 Access forbidden: ${errorCode}`);
    }

    // Handle 429 Rate Limiting with retry
    if (error.response?.status === 429 && !originalRequest._retryCount) {
      const retryAfter = parseInt(error.response.headers['retry-after'] || '1');
      originalRequest._retryCount = 1;

      console.warn(`⏰ Rate limited. Retrying after ${retryAfter}s`);
      await new Promise(resolve =>
        setTimeout(resolve, Math.min(retryAfter * 1000, 5000))
      );
      return api.request(originalRequest);
    }

    // Handle 5xx Server Errors with retry for GET requests
    if (
      error.response?.status >= 500 &&
      !originalRequest._retryCount &&
      originalRequest.method?.toLowerCase() === 'get'
    ) {
      originalRequest._retryCount = 1;

      console.warn('🔄 Server error, retrying GET request...');
      await new Promise(resolve => setTimeout(resolve, API_CONFIG.retryDelay));
      return api.request(originalRequest);
    }

    return Promise.reject(error);
  }
);

// ===============================================
// HELPER FUNCTIONS
// ===============================================

const getCurrentLocation = () => {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error('Geolocation not supported'));
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        resolve({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          accuracy: position.coords.accuracy,
          timestamp: position.timestamp
        });
      },
      (error) => {
        reject(error);
      },
      {
        timeout: 5000,
        enableHighAccuracy: false,
        maximumAge: 300000 // 5 minutes
      }
    );
  });
};

// ===============================================
// AUTHENTICATION SERVICE
// ===============================================

export const authService = {
  // Employee login
  async login(credentials) {
    try {
      console.log('🔑 Employee login attempt...');

      const response = await api.post('/auth/login/employee', {
        email: credentials.email,
        password: credentials.password,
        location: await getCurrentLocation().catch(() => null),
        deviceInfo: getClientInfo(),
      });

      if (response.data.success && response.data.data) {
        const { token, user, session } = response.data.data;
        
        // Store based on remember me preference
        const storage = credentials.rememberMe ? localStorage : sessionStorage;
        storage.setItem(STORAGE_KEYS.auth.token, token);
        storage.setItem(STORAGE_KEYS.auth.user, JSON.stringify(user));
        storage.setItem(STORAGE_KEYS.auth.lastActivity, Date.now().toString());
        
        if (credentials.rememberMe) {
          localStorage.setItem(STORAGE_KEYS.auth.rememberMe, 'true');
        }

        console.log('✅ Employee login successful');
        return { success: true, token, user, session };
      }

      throw new Error(response.data.message || 'Login failed');
    } catch (error) {
      console.error('❌ Employee login failed:', error);
      const message = error.response?.data?.message || error.message || 'Login failed';
      return { success: false, error: message };
    }
  },

  // Admin login
  async adminLogin(credentials) {
    try {
      console.log('🛡️ Admin login attempt...', {
        email: credentials.email,
        hasPassword: !!credentials.password,
        rememberMe: credentials.rememberMe,
        apiUrl: API_CONFIG.baseURL
      });

      const requestData = {
        email: credentials.email,
        password: credentials.password,
        location: await getCurrentLocation().catch(() => null),
        deviceInfo: getClientInfo(),
      };

      console.log('📤 Sending login request:', {
        url: '/auth/login/admin',
        method: 'POST',
        hasLocation: !!requestData.location,
        hasDeviceInfo: !!requestData.deviceInfo
      });

      const response = await api.post('/auth/login/admin', requestData);

      console.log('📥 Login response received:', {
        status: response.status,
        statusText: response.statusText,
        hasData: !!response.data,
        dataSuccess: response.data?.success,
        hasToken: !!response.data?.data?.token
      });

      if (response.data.success && response.data.data) {
        const { token, user, session } = response.data.data;
        
        console.log('🔑 Storing admin auth data:', {
          hasToken: !!token,
          tokenPrefix: token?.substring(0, 20) + '...',
          userRole: user?.role,
          rememberMe: credentials.rememberMe
        });
        
        // Store based on remember me preference
        const storage = credentials.rememberMe ? localStorage : sessionStorage;
        storage.setItem(STORAGE_KEYS.auth.token, token);
        storage.setItem(STORAGE_KEYS.auth.user, JSON.stringify(user));
        storage.setItem(STORAGE_KEYS.auth.lastActivity, Date.now().toString());
        
        if (credentials.rememberMe) {
          localStorage.setItem(STORAGE_KEYS.auth.rememberMe, 'true');
        }

        // Verify storage worked
        const storedToken = getStoredToken();
        console.log('✅ Admin login successful - Token stored:', !!storedToken);
        
        return { success: true, token, user, session };
      }

      console.log('⚠️ Login response not successful:', response.data);
      throw new Error(response.data.message || 'Admin login failed');
    } catch (error) {
      console.error('❌ Admin login failed:', {
        message: error.message,
        status: error.response?.status,
        statusText: error.response?.statusText,
        responseData: error.response?.data,
        stack: error.stack?.substring(0, 200) + '...'
      });
      const message = error.response?.data?.message || error.message || 'Admin login failed';
      return { success: false, error: message };
    }
  },

  // Get current user
  async getCurrentUser() {
    try {
      const response = await api.get('/auth/me');

      if (response.data.success && response.data.data) {
        const user = response.data.data;
        
        // Update stored user data
        const storage = localStorage.getItem(STORAGE_KEYS.auth.token) ? localStorage : sessionStorage;
        storage.setItem(STORAGE_KEYS.auth.user, JSON.stringify(user));
        
        return { success: true, user };
      }

      throw new Error('Failed to get current user');
    } catch (error) {
      console.error('❌ Get current user failed:', error);
      const message = error.response?.data?.message || error.message || 'Failed to get user data';
      return { success: false, error: message };
    }
  },

  // Logout
  async logout() {
    try {
      await api.post('/auth/logout');
    } catch (error) {
      console.warn('Logout API call failed:', error);
    } finally {
      clearAuthData();
      cache.clear();
      console.log('✅ Logout successful');
    }
  },

  // Refresh token
  async refreshToken() {
    try {
      const response = await api.post('/auth/sessions/extend', {
        location: await getCurrentLocation().catch(() => null)
      });

      if (response.data.success && response.data.data?.token) {
        const { token, user } = response.data.data;
        
        // Update stored data
        const storage = localStorage.getItem(STORAGE_KEYS.auth.token) ? localStorage : sessionStorage;
        storage.setItem(STORAGE_KEYS.auth.token, token);
        storage.setItem(STORAGE_KEYS.auth.user, JSON.stringify(user));
        storage.setItem(STORAGE_KEYS.auth.lastActivity, Date.now().toString());

        return { success: true, token, user };
      }

      throw new Error('Token refresh failed');
    } catch (error) {
      clearAuthData();
      return { success: false, error: error.message };
    }
  },

  // Check authentication
  isAuthenticated() {
    const token = getStoredToken();
    return !!token && !isTokenExpired(token);
  },

  // Get stored user
  getUser() {
    return getStoredUser();
  },
};

// ===============================================
// EMPLOYEE SERVICE
// ===============================================

export const employeeService = {
  async getProfile() {
    const cacheKey = 'employee_profile';
    const cached = cache.get(cacheKey);
    if (cached) return cached;

    const response = await api.get('/profile');
    cache.set(cacheKey, response.data);
    return response.data;
  },

  async updateProfile(profileData) {
    const response = await api.put('/profile/personal', profileData);
    cache.delete('employee_profile');
    return response.data;
  },

  async updateProfilePhoto(photoFile) {
    const formData = new FormData();
    formData.append('photo', photoFile);

    const response = await api.post('/profile/photo', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    cache.delete('employee_profile');
    return response.data;
  },

  async changePassword(passwordData) {
    const response = await api.post('/profile/change-password', passwordData);
    return response.data;
  },

  async getAttendanceRecords(params = {}) {
    const response = await api.get('/attendance/my-records', { params });
    return response.data;
  },

  async clockIn(attendanceData) {
    const response = await api.post('/attendance/clock-in', attendanceData);
    return response.data;
  },

  async clockOut(attendanceData) {
    const response = await api.post('/attendance/clock-out', attendanceData);
    return response.data;
  },

  async getTodayAttendance() {
    const response = await api.get('/attendance/today');
    return response.data;
  },

  async submitLeaveRequest(leaveData) {
    const response = await api.post('/leaves/request', leaveData);
    return response.data;
  },

  async getLeaveRequests(params = {}) {
    const response = await api.get('/leaves/my-requests', { params });
    return response.data;
  },

  async getLeaveBalance() {
    const response = await api.get('/profile/leave-balance');
    return response.data;
  },
};

// ===============================================
// ADMIN SERVICE - OPTIMIZED
// ===============================================

export const adminService = {
  // Dashboard
  async getDashboardStats() {
    try {
      console.log('📊 Fetching dashboard stats...');
      const response = await api.get('/admin/dashboard/stats');
      console.log('✅ Dashboard stats received');
      return response.data;
    } catch (error) {
      console.error('❌ Dashboard stats error:', error);
      return {
        success: true,
        stats: {
          totalEmployees: 0,
          todayAttendance: 0,
          pendingLeaves: 0,
          activeFieldWork: 0,
          securityAlerts: 0,
        },
        recentActivity: [],
        timestamp: new Date().toISOString(),
        message: 'Dashboard data temporarily unavailable',
        _fallback: true,
      };
    }
  },

  async getDashboardActivities() {
    try {
      const response = await api.get('/admin/dashboard/activities');
      return response.data;
    } catch (error) {
      console.error('❌ Dashboard activities error:', error);
      return {
        success: true,
        activities: [],
        timestamp: new Date().toISOString(),
        message: 'Recent activities temporarily unavailable',
        _fallback: true,
      };
    }
  },

  // Employee Management
  async getAllEmployees(params = {}) {
    try {
      console.log('👥 Fetching employees...');
      const response = await api.get('/admin/employees', {
        params: {
          page: params.page || 1,
          limit: params.limit || 10,
          search: params.search || '',
          department: params.department || '',
          ...params,
        },
      });
      console.log('✅ Employees fetched');
      return response.data;
    } catch (error) {
      console.error('❌ Error fetching employees:', error);
      throw error;
    }
  },

  async getEmployeeDetails(employeeId) {
    try {
      const response = await api.get(`/admin/employees/${employeeId}`);
      return response.data;
    } catch (error) {
      console.error(`❌ Error fetching employee ${employeeId}:`, error);
      throw error;
    }
  },

  async createEmployee(employeeData) {
    try {
      console.log('➕ Creating new employee');

      // Basic validation
      const requiredFields = [
        'name',
        'email',
        'password',
        'department',
        'position',
      ];
      const missingFields = requiredFields.filter(
        field => !employeeData[field]
      );

      if (missingFields.length > 0) {
        throw new Error(`Missing required fields: ${missingFields.join(', ')}`);
      }

      const response = await api.post('/admin/employees', employeeData);
      console.log('✅ Employee created');
      cache.clear('employees_');
      return response.data;
    } catch (error) {
      console.error('❌ Error creating employee:', error);
      throw error;
    }
  },

  async updateEmployee(employeeId, employeeData) {
    try {
      console.log(`🔄 Updating employee ${employeeId}`);
      const response = await api.put(
        `/admin/employees/${employeeId}`,
        employeeData
      );
      console.log('✅ Employee updated');
      cache.clear('employees_');
      return response.data;
    } catch (error) {
      console.error(`❌ Error updating employee ${employeeId}:`, error);
      throw error;
    }
  },

  async deleteEmployee(employeeId) {
    try {
      const response = await api.delete(`/admin/employees/${employeeId}`);
      cache.clear('employees_');
      return response.data;
    } catch (error) {
      console.error(`❌ Error deactivating employee ${employeeId}:`, error);
      throw error;
    }
  },

  async toggleEmployeeStatus(employeeId) {
    try {
      console.log(`🔄 Toggling status for employee ${employeeId}`);
      const employeeDetails = await this.getEmployeeDetails(employeeId);
      const currentStatus = employeeDetails.employee.is_active;

      const response = await this.updateEmployee(employeeId, {
        is_active: !currentStatus,
      });

      console.log('✅ Employee status toggled');
      return response.data;
    } catch (error) {
      console.error(`❌ Error toggling employee ${employeeId} status:`, error);
      throw error;
    }
  },

  async resetEmployeePassword(employeeId) {
    try {
      console.log(`🔑 Resetting password for employee ${employeeId}`);

      const tempPassword = this.generateTempPassword();
      const response = await this.updateEmployee(employeeId, {
        password: tempPassword,
      });

      return {
        ...response,
        newPassword: tempPassword,
      };
    } catch (error) {
      console.error(
        `❌ Error resetting password for employee ${employeeId}:`,
        error
      );
      throw error;
    }
  },

  generateTempPassword() {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789';
    let password = '';
    for (let i = 0; i < 8; i++) {
      password += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return password;
  },

  async getDepartments() {
    try {
      const cacheKey = 'departments_list';
      const cached = cache.get(cacheKey);
      if (cached) return cached;

      const employees = await this.getAllEmployees({ limit: 1000 });
      const departments = [
        ...new Set(
          employees.employees
            ?.map(emp => emp.department)
            .filter(dept => dept && dept.trim())
        ),
      ].sort();

      cache.set(cacheKey, departments);
      return departments;
    } catch (error) {
      console.error('❌ Error fetching departments:', error);
      return [
        'Human Resources',
        'Engineering',
        'Sales',
        'Marketing',
        'Finance',
        'Operations',
        'Customer Support',
        'Management',
      ];
    }
  },

  // Leave Management
  async getLeaveRequests(params = {}) {
    try {
      const response = await api.get('/admin/leave-requests', {
        params: {
          page: params.page || 1,
          limit: params.limit || 10,
          status: params.status || '',
          department: params.department || '',
          ...params,
        },
      });
      return response.data;
    } catch (error) {
      console.error('❌ Error fetching leave requests:', error);
      throw error;
    }
  },

  async updateLeaveRequest(leaveRequestId, status, adminRemarks = '') {
    try {
      const response = await api.put(
        `/admin/leave-requests/${leaveRequestId}`,
        {
          status,
          admin_remarks: adminRemarks,
        }
      );
      return response.data;
    } catch (error) {
      console.error(
        `❌ Error updating leave request ${leaveRequestId}:`,
        error
      );
      throw error;
    }
  },

  // Attendance Reports
  async getAttendanceReports(params = {}) {
    try {
      const response = await api.get('/admin/attendance/reports', {
        params: {
          start_date: params.start_date || '',
          end_date: params.end_date || '',
          employee_id: params.employee_id || '',
          department: params.department || '',
          ...params,
        },
      });
      return response.data;
    } catch (error) {
      console.error('❌ Error fetching attendance reports:', error);
      throw error;
    }
  },

  // Security
  async getSecurityAnalytics(timeRange = '7d') {
    try {
      const response = await api.get('/admin/security/analytics', {
        params: { timeRange },
      });
      return response.data;
    } catch (error) {
      console.error('❌ Error fetching security analytics:', error);
      throw error;
    }
  },

  // Session Management
  async getAllSessions(params = {}) {
    try {
      const response = await api.get('/admin/sessions', { params });
      return response.data;
    } catch (error) {
      console.error('❌ Error fetching sessions:', error);
      throw error;
    }
  },

  async getSessionStats() {
    try {
      const response = await api.get('/admin/sessions/stats');
      return response.data;
    } catch (error) {
      console.error('❌ Error fetching session stats:', error);
      throw error;
    }
  },

  async terminateSession(sessionId, reason = '') {
    try {
      const response = await api.post(
        `/admin/sessions/${sessionId}/terminate`,
        { reason }
      );
      return response.data;
    } catch (error) {
      console.error(`❌ Error terminating session ${sessionId}:`, error);
      throw error;
    }
  },

  // Bulk Operations
  async bulkUpdateEmployees(employeeIds, updateData) {
    try {
      console.log('🔄 Bulk updating employees');

      const results = await Promise.allSettled(
        employeeIds.map(id => this.updateEmployee(id, updateData))
      );

      const successful = results.filter(r => r.status === 'fulfilled').length;
      const failed = results.filter(r => r.status === 'rejected').length;

      console.log(
        `✅ Bulk update completed: ${successful} successful, ${failed} failed`
      );

      return {
        success: true,
        successful,
        failed,
        total: employeeIds.length,
      };
    } catch (error) {
      console.error('❌ Error in bulk update:', error);
      throw error;
    }
  },

  // Short Leave Debts
  async getShortLeaveDebts(params = {}) {
    try {
      console.log('📊 Fetching short leave debts...');
      const response = await api.get('/admin/short-leave-debts', { params });
      console.log('✅ Short leave debts received');
      return response.data;
    } catch (error) {
      console.error('❌ Error fetching short leave debts:', error);
      throw error;
    }
  },

  async getShortLeaveDebtSummary(params = {}) {
    try {
      console.log('📊 Fetching short leave debts summary...');
      const response = await api.get('/admin/short-leave-debts/summary', { params });
      console.log('✅ Short leave debts summary received');
      return response.data;
    } catch (error) {
      console.error('❌ Error fetching short leave debts summary:', error);
      throw error;
    }
  },
};

// ===============================================
// HELPER FUNCTIONS
// ===============================================

export const handleApiError = (error, context = '') => {
  const errorInfo = {
    context,
    status: error.response?.status,
    message: error.response?.data?.message || error.message,
    code: error.response?.data?.code,
  };

  console.error(`API Error ${context}:`, errorInfo);

  switch (error.response?.status) {
    case 400:
      return (
        error.response.data?.message ||
        'Invalid request. Please check your input.'
      );
    case 401:
      return 'Authentication required. Please log in again.';
    case 403:
      return error.response.data?.message || 'Access denied.';
    case 404:
      return 'The requested resource was not found.';
    case 409:
      return error.response.data?.message || 'A conflict occurred.';
    case 422:
      return error.response.data?.message || 'Validation failed.';
    case 429:
      return 'Too many requests. Please wait and try again.';
    case 500:
      return 'Server error occurred. Please try again later.';
    default:
      return error.message || 'An unexpected error occurred.';
  }
};

export const validateEmail = email => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

export const validatePassword = password => {
  return password && password.length >= 6;
};

export const createFormData = (data, files = {}) => {
  const formData = new FormData();

  Object.entries(data).forEach(([key, value]) => {
    if (value !== null && value !== undefined) {
      formData.append(
        key,
        typeof value === 'object' ? JSON.stringify(value) : value
      );
    }
  });

  Object.entries(files).forEach(([key, file]) => {
    if (file instanceof File) {
      formData.append(key, file);
    }
  });

  return formData;
};

// ===============================================
// DATE/TIME HELPERS
// ===============================================

export const formatDate = (dateString, options = {}) => {
  if (!dateString) return '';

  const defaultOptions = {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    ...options,
  };

  try {
    return new Date(dateString).toLocaleDateString('en-US', defaultOptions);
  } catch (error) {
    return dateString;
  }
};

export const formatDateTime = dateTimeString => {
  if (!dateTimeString) return '';

  try {
    return new Date(dateTimeString).toLocaleString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    });
  } catch (error) {
    return dateTimeString;
  }
};

export const getRelativeTime = dateString => {
  if (!dateString) return '';

  try {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 30) return `${diffDays}d ago`;

    return formatDate(dateString);
  } catch (error) {
    return dateString;
  }
};

// ===============================================
// EXPORTS
// ===============================================

// Cache utilities
export const getCachedData = key => cache.get(key);
export const setCachedData = (key, data) => cache.set(key, data);
export const clearCache = pattern => cache.clear(pattern);

// Default export
// Export aliases for compatibility
export const apiClient = api;

export default api;
